/*
 * Lets users choose how a pathname exclusion's text matches, with the explanation of every
 * mode one focusable help icon away on the Match label line instead of an inline helper line
 * that shifted the row. The label and the dropdown are separate components so the editor can
 * put this label on the same line as the pattern input's label, and the dropdown on the same
 * line as the input. Also owns the mode-to-string-key mapping so the dropdown, the input
 * placeholder, and each entry's mode descriptor always use the same localized wording.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import helpTokens from '@amzn/meridian-tokens/base/icon/help';
import { useTranslation } from 'src/arb-wrapper';
import { type PathnameListDraft } from 'src/hooks/usePathnameListDraft';
import { PATHNAME_MATCH_MODE_ORDER, type PathnameMatchMode, PathnameMatchModes } from 'src/utils/pathnameMatchMode';

const MATCH_MODE_KEY_SUFFIXES: Record<PathnameMatchMode, string> = {
  [PathnameMatchModes.Contains]: 'contains',
  [PathnameMatchModes.Exactly]: 'exactly',
  [PathnameMatchModes.StartsWith]: 'starts_with',
  [PathnameMatchModes.EndsWith]: 'ends_with',
  [PathnameMatchModes.Advanced]: 'advanced',
};

/** Fits the longest mode label ("Exactly matches") while leaving the pattern input most of the row. */
export const MATCH_MODE_SELECT_WIDTH = 176;

/** Meridian's recommended ceiling for tooltip copy. */
const HELP_TOOLTIP_MAX_WIDTH = 300;

const STANDARD_ICON_SIZE = 24;
const UTILITY_ICON_SIZE = 16;

/**
 * Meridian ships no small help glyph and Icon has no size prop, so the standard 24px glyph is
 * redrawn at the 16px utility size (a viewBox keeps its proportions) to sit inline with b200 text.
 */
const smallHelpTokens = {
  iconHelpData: helpTokens.iconHelpData.replace(
    `width='${STANDARD_ICON_SIZE}' height='${STANDARD_ICON_SIZE}'`,
    `width='${UTILITY_ICON_SIZE}' height='${UTILITY_ICON_SIZE}' viewBox='0 0 ${STANDARD_ICON_SIZE} ${STANDARD_ICON_SIZE}'`,
  ),
  iconHelpWidth: UTILITY_ICON_SIZE,
  iconHelpHeight: UTILITY_ICON_SIZE,
};

export const getMatchModeLabelKey = (mode: PathnameMatchMode): string =>
  `box_folder_exclusions_match_mode_${MATCH_MODE_KEY_SUFFIXES[mode]}`;

export const getPathnamePlaceholderKey = (mode: PathnameMatchMode): string =>
  `box_folder_exclusions_pathname_placeholder_${MATCH_MODE_KEY_SUFFIXES[mode]}`;

interface PathnameMatchModeLabelProps {
  testIdPrefix: string;
}

export const PathnameMatchModeLabel: React.FC<PathnameMatchModeLabelProps> = ({ testIdPrefix }) => {
  const { t } = useTranslation();

  return (
    <Row alignmentVertical="center" spacing="100">
      <Text type="b200">{t('box_folder_exclusions_match_mode_label')}</Text>
      <Tooltip maxWidth={HELP_TOOLTIP_MAX_WIDTH} position="top" title={t('box_folder_exclusions_match_mode_help')}>
        <Button
          aria-label={t('box_folder_exclusions_match_mode_help_label')}
          data-testid={`${testIdPrefix}-mode-help`}
          size="small"
          type="icon"
        >
          <Icon tokens={smallHelpTokens} />
        </Button>
      </Tooltip>
    </Row>
  );
};

interface PathnameMatchModeSelectProps {
  draft: PathnameListDraft;
  isDisabled: boolean;
  testIdPrefix: string;
}

export const PathnameMatchModeSelect: React.FC<PathnameMatchModeSelectProps> = ({
  draft,
  isDisabled,
  testIdPrefix,
}) => {
  const { t } = useTranslation();

  return (
    <Select
      aria-label={t('box_folder_exclusions_match_mode_label')}
      data-testid={`${testIdPrefix}-mode-select`}
      disabled={isDisabled}
      value={draft.mode}
      width={MATCH_MODE_SELECT_WIDTH}
      onChange={draft.setMode}
    >
      {PATHNAME_MATCH_MODE_ORDER.map((mode) => (
        <SelectOption key={mode} label={t(getMatchModeLabelKey(mode))} value={mode} />
      ))}
    </Select>
  );
};
